// 分享連結視窗裡的「製播會 / 控師唯讀連結」一段（只有 Cue 庫專案）：
//   產生一條專屬連結，傳給製播會或控師 —— 不用登入就能看舞台、歌單、全場機關動作和機關總表，什麼都不能改、不能留言。
//   停用 = 刪掉這條連結，已經傳出去的馬上打不開。誰能產生 / 停用：跟一般分享連結一樣（製作、導演）。
import { useState } from 'react';
import { Copy, ExternalLink, Eye, Tag, Trash2, Users } from 'lucide-react';
import * as api from '../lib/api';
import type { ShareLink } from '../lib/api';
import { toast } from '../lib/toast';
import { crewUrl } from '../lib/siteUrl';
import { CREW_TEXT } from '../lib/crewText';
import { parseServerTime } from '../lib/serverTime';
import Icon from './Icon';
import { confirmDialog, promptDialog } from './dialogs';
import './CrewLinksPanel.css';

interface Props {
  projectId: string;
  /** 這個專案現有的製播會連結（kind = 'crew'） */
  links: ShareLink[];
  onChanged: () => void | Promise<void>;
}

export default function CrewLinksPanel({ projectId, links, onChanged }: Props) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(0);   // 0 = 不會過期
  const [media, setMedia] = useState(false);   // 預設不放影片和音樂
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const made = await api.createShareLink(projectId, { kind: 'crew', crewMedia: media, label: label.trim() || null, expiresInDays: days > 0 ? days : undefined });
      setLabel('');
      setMedia(false);
      await onChanged();
      toast.success(CREW_TEXT.created);
      await copy(crewUrl(made.token), false);
    } catch (e) {
      toast.error(CREW_TEXT.createFailed + (e instanceof Error ? e.message : String(e)));
    } finally { setBusy(false); }
  }

  async function copy(url: string, announce = true) {
    try {
      await navigator.clipboard.writeText(url);
      if (announce) toast.success('已複製連結');
    } catch {
      void promptDialog({ title: '複製這個連結', message: '瀏覽器不讓我們直接複製，請把下面的連結選起來複製（手機長按）。', defaultValue: url, readOnly: true, confirmLabel: '好', cancelLabel: null });
    }
  }

  async function stop(l: ShareLink) {
    if (!(await confirmDialog({
      title: CREW_TEXT.stopTitle,
      message: CREW_TEXT.stopMessage,
      confirmLabel: CREW_TEXT.stopConfirm,
      destructive: true,
    }))) return;
    try {
      await api.deleteShareLink(projectId, l.token);
      toast.success(CREW_TEXT.stopped);
      await onChanged();
    } catch (e) { toast.error(CREW_TEXT.stopFailed + (e instanceof Error ? e.message : String(e))); }
  }

  return (
    <section className="crew-links" aria-label={CREW_TEXT.panelTitle}>
      <h3><Icon icon={Users} size="sm" /> {CREW_TEXT.panelTitle}</h3>
      <p className="crew-links__hint">{CREW_TEXT.panelHint}</p>
      <div className="crew-links__create">
        <label>
          <span>{CREW_TEXT.nameLabel}</span>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder={CREW_TEXT.namePlaceholder} maxLength={40} />
        </label>
        <label>
          <span>{CREW_TEXT.expiryLabel}</span>
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={0}>不會過期</option>
            <option value={7}>7 天</option>
            <option value={30}>30 天</option>
            <option value={90}>90 天</option>
          </select>
        </label>
        <button type="button" className="btn btn--primary btn--sm" onClick={create} disabled={busy}>{busy ? '建立中…' : CREW_TEXT.createButton}</button>
      </div>
      <label className="crew-links__media">
        <input type="checkbox" checked={media} onChange={e => setMedia(e.target.checked)} />
        <span><strong>{CREW_TEXT.mediaLabel}</strong>{CREW_TEXT.mediaHint}</span>
      </label>
      {links.length === 0 ? (
        <div className="crew-links__empty">{CREW_TEXT.none}</div>
      ) : (
        <ul className="crew-links__list">
          {links.map(l => {
            const url = crewUrl(l.token);
            const expired = !!l.expiresAt && parseServerTime(l.expiresAt) < Date.now();
            return (
              <li key={l.token} className={'crew-links__row' + (expired ? ' is-expired' : '')}>
                <div className="crew-links__main">
                  {l.label && <div className="crew-links__label"><Icon icon={Tag} size="xs" /> {l.label}</div>}
                  <code className="crew-links__url">{url}</code>
                  <div className="crew-links__meta">
                    <span><Icon icon={Eye} size="xs" /> 打開過 {l.viewCount} 次</span>
                    <span>{l.crewMedia ? CREW_TEXT.mediaOn : CREW_TEXT.mediaOff}</span>
                    <span>{l.expiresAt ? (expired ? '已過期' : `到 ${new Date(parseServerTime(l.expiresAt)).toLocaleDateString('zh-TW')} 為止`) : '不會過期'}</span>
                  </div>
                </div>
                <div className="crew-links__actions">
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => { void copy(url); }} title="複製連結" aria-label="複製連結"><Icon icon={Copy} /></button>
                  <a className="btn btn--ghost btn--sm" href={url} target="_blank" rel="noopener noreferrer" title="用新分頁打開看看" aria-label="用新分頁打開看看"><Icon icon={ExternalLink} /></a>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => { void stop(l); }} title={CREW_TEXT.stopConfirm} aria-label={CREW_TEXT.stopConfirm}><Icon icon={Trash2} /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
